import { formatPrice } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

// Kuryer çağırılandan çatdırılana qədər müştəri üçün hamısı «yoldadır»:
// Wolt-un daxili mərhələləri (götürməyə gedir, mağazanın yanındadır…)
// müştəriyə heç nə demir.
const ON_WAY = new Set([
  "requested", "received", "pickup_started", "pickup_arrival",
  "picked_up", "dropoff_started", "dropoff_arrival",
]);

/**
 * Ödənişdən sonrakı səhifələrdə sifariş xülasəsi (təşəkkür + ödəniş nəticəsi).
 * İki yerdə eyni olmalıdır — çatdırılma haqqı birində görünüb o birində
 * görünməsə, müştəri məbləğin niyə fərqli olduğunu soruşar.
 */
export const OrderSummary = ({ order }) => {
  const { t } = useLanguage();
  const fee = order.delivery_fee || 0;
  const detailed = fee > 0 || order.discount_amount > 0;
  const method = order.delivery_method;
  const wolt = order.wolt || {};
  const addr = order.address || {};
  const where = [addr.city, addr.line].filter(Boolean).join(", ");
  const recipient = order.recipient || null;
  const pref = order.delivery_pref && order.delivery_pref !== "asap" ? order.delivery_pref : null;

  const woltText = wolt.status === "delivered"
    ? t("wolt_delivered")
    : ON_WAY.has(wolt.status) ? t("wolt_on_way") : t("wolt_waiting");

  return (
    <>
      <div className="space-y-3">
        {order.items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span>{it.name} ×{it.quantity}</span>
            <span>{formatPrice(it.price * it.quantity)}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-brand-line mt-5 pt-5 space-y-2">
        {detailed && (
          <div className="flex justify-between text-sm">
            <span className="overline">{t("subtotal")}</span>
            <span>{formatPrice(order.subtotal)}</span>
          </div>
        )}
        {order.discount_amount > 0 && (
          <div className="flex justify-between text-sm text-brand-oxid">
            <span className="overline">{t("discount")} · {order.discount_percent}%</span>
            <span>−{formatPrice(order.discount_amount)}</span>
          </div>
        )}
        {fee > 0 && (
          <div className="flex justify-between text-sm" data-testid="summary-delivery">
            <span className="overline">{t("delivery_fee_label")}</span>
            <span>{formatPrice(fee)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="overline">{detailed ? t("order_total") : t("subtotal")}</span>
          <span className="font-serif text-xl">{formatPrice(order.total)}</span>
        </div>
      </div>

      <div className="mt-5 text-xs text-brand-muted space-y-0.5" data-testid="summary-address">
        <p>{order.customer?.name} · {order.customer?.phone}</p>
        <p>
          {method === "pickup" ? t("delivery_pickup") : where}
          {method !== "pickup" && addr.flat ? ` · ${addr.flat}` : ""}
        </p>
        {method !== "pickup" && addr.note && <p>{addr.note}</p>}
        {method !== "pickup" && recipient?.phone && (
          <p data-testid="summary-recipient">
            {t("summary_recipient")}: {recipient.name} · {recipient.phone}
          </p>
        )}
        {method !== "pickup" && pref && (
          <p data-testid="summary-pref">{t("summary_pref")}: {t(`pref_${pref}`)}</p>
        )}
      </div>

      {method === "wolt" && (
        <div className="mt-4 text-sm" data-testid="summary-wolt">
          <p className="text-brand-sub">{woltText}</p>
          {wolt.tracking_url && (
            <a
              href={wolt.tracking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 underline underline-offset-4"
              data-testid="summary-track"
            >
              {t("track_courier")}
            </a>
          )}
        </div>
      )}
    </>
  );
};
